import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import fileService from "@/services/fileService";
import { checkPlanLimits, PlanLimitError } from "@/lib/planLimits";

export type KnowledgeItemType = "snippet" | "file" | "link";

export interface KnowledgeItem {
  id: string;
  organization_id: string;
  title: string;
  description: string | null;
  type: KnowledgeItemType;
  content: string | null;
  file_path: string | null;
  file_name: string | null;
  file_type: string | null;
  file_size: number | null;
  url: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  is_active: boolean | null;
  ai_processed?: boolean | null;
  ai_file_id?: string | null;
  ai_processing_error?: string | null;
  vector_store_id?: string | null;
  vector_store_status?: string | null;
  agent_id?: string | null;
  voice_agent_id?: string | null;
  vapi_file_id?: string | null;
  vapi_upload_status?: string | null;
}

export const useKnowledgeItems = (
  type?: KnowledgeItemType,
  agentId?: string | null,
  agentType?: "regular" | "voice"
) => {
  const [items, setItems] = useState<KnowledgeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { organization } = useAuth();
  const { toast } = useToast();

  const fetchItems = useCallback(async () => {
    if (!organization?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      let query = supabase
        .from("knowledge_items")
        .select("*")
        .eq("organization_id", organization.id);

      if (type) {
        query = query.eq("type", type);
      }

      if (agentId !== undefined) {
        if (agentId === null) {
          // When null, fetch items with no agent (organization-level)
          query = query.is("agent_id", null).is("voice_agent_id", null);
        } else {
          // When a specific agent ID is provided, filter by the appropriate field
          if (agentType === "voice") {
            query = query.eq("voice_agent_id", agentId);
          } else {
          query = query.eq("agent_id", agentId);
          }
        }
      }

      query = query.order("created_at", { ascending: false });

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      setItems(data as KnowledgeItem[]);
    } catch (err: any) {
      setError(err.message || "Failed to fetch knowledge items");
      toast({
        variant: "destructive",
        title: "Error fetching knowledge items",
        description: err.message || "An unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id, type, agentId, agentType, toast]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return { items, isLoading, error, refetch: fetchItems };
};

export const useCreateKnowledgeItem = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { organization, user } = useAuth();
  const { toast } = useToast();

  const createItem = async (item: Partial<KnowledgeItem>) => {
    if (!organization?.id) {
      setError("No organization found");
      toast({
        variant: "destructive",
        title: "Operation failed",
        description: "No organization found",
      });
      return null;
    }

    // Restrict by plan: check knowledge items limit
    let limitError: PlanLimitError | null = null;
    try {
      limitError = await checkPlanLimits(organization.id, 'knowledge_items');
    } catch (err) {
      setError(limitError.message);
      toast({
        variant: "destructive",
        title: "Plan Limit Reached",
        description: limitError.message,
      });
      return null;
    }

    // Ensure required fields are present
    if (!item.title) {
      setError("Title is required");
      toast({
        variant: "destructive",
        title: "Operation failed",
        description: "Title is required",
      });
      return null;
    }

    if (!item.type) {
      setError("Type is required");
      toast({
        variant: "destructive",
        title: "Operation failed",
        description: "Type is required",
      });
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const insertData = {
        organization_id: organization.id,
        title: item.title,
        type: item.type,
        description: item.description || null,
        content: item.content || null,
        file_path: item.file_path || null,
        file_name: item.file_name || null,
        file_type: item.file_type || null,
        file_size: item.file_size || null,
        url: item.url || null,
        created_by: user?.id || null,
        is_active: true,
        agent_id: item.agent_id || null,
        voice_agent_id: item.voice_agent_id || null,
      };

      const { data, error: insertError } = await supabase
        .from("knowledge_items")
        .insert(insertData)
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      toast({
        title: "Knowledge item created",
        description: "Your knowledge item has been created successfully",
      });

      // Increment knowledge_items_count for the organization
      if (organization?.id) {
        try {
          const { data: orgData, error: orgError } = await supabase
            .from('organizations')
            .select('knowledge_items_count')
            .eq('id', organization.id)
            .single();
          if (!orgError && orgData) {
            const currentCount = orgData.knowledge_items_count || 0;
            await supabase
              .from('organizations')
              .update({ knowledge_items_count: currentCount + 1 })
              .eq('id', organization.id);
            console.log('[KNOWLEDGE] Incremented knowledge_items_count for organization:', organization.id);
          }
        } catch (err) {
          console.error('Failed to increment knowledge_items_count:', err);
        }
      }

      return data;
    } catch (err: any) {
      setError(err.message || "Failed to create knowledge item");
      toast({
        variant: "destructive",
        title: "Failed to create knowledge item",
        description: err.message || "An unexpected error occurred",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { createItem, isLoading, error };
};

export const useDeleteKnowledgeItem = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const deleteItem = async (
    id: string,
    type: KnowledgeItemType,
    filePath?: string
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      // First, get the knowledge item to check if it has an OpenAI file ID
      const { data: item, error: fetchError } = await supabase
        .from("knowledge_items")
        .select("*")
        .eq("id", id)
        .single();

      if (fetchError) {
        console.error("Error fetching knowledge item:", fetchError);
        // Continue with deletion even if fetch fails
      }

      // If the item has been processed for AI search, delete from OpenAI
      if (item?.ai_processed && item?.ai_file_id) {
        console.log(`Deleting file ${item.ai_file_id} from OpenAI`);
        try {
          const deleteResult = await fileService.deleteFile(item.ai_file_id);
          if (!deleteResult.success) {
            console.error(
              "Failed to delete file from OpenAI:",
              deleteResult.error
            );
            // Continue with deletion even if OpenAI deletion fails
          } else {
            console.log("File successfully deleted from OpenAI");
          }
        } catch (openaiError) {
          console.error("Error deleting file from OpenAI:", openaiError);
          // Continue with deletion even if OpenAI deletion fails
        }
      }

      // If it's a file type, also delete the file from storage
      if (type === "file" && filePath) {
        const { error: storageError } = await supabase.storage
          .from("knowledge_files")
          .remove([filePath]);

        if (storageError) {
          console.error("Error deleting file from storage:", storageError);
          // Continue with deleting the record even if file deletion fails
        }
      }

      // Delete the knowledge item from the database
      const { error: deleteError } = await supabase
        .from("knowledge_items")
        .delete()
        .eq("id", id);

      if (deleteError) {
        throw deleteError;
      }

      toast({
        title: "Knowledge item deleted",
        description: "Your knowledge item has been deleted successfully",
      });

      return true;
    } catch (err: any) {
      setError(err.message || "Failed to delete knowledge item");
      toast({
        variant: "destructive",
        title: "Failed to delete knowledge item",
        description: err.message || "An unexpected error occurred",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { deleteItem, isLoading, error };
};

export const useUpdateKnowledgeItem = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const updateItem = async (item: Partial<KnowledgeItem> & { id: string }) => {
    if (!item.id) {
      setError("Item ID is required");
      toast({
        variant: "destructive",
        title: "Operation failed",
        description: "Item ID is required",
      });
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Prepare the update data
      const updateData: any = {};

      if (item.title !== undefined) updateData.title = item.title;
      if (item.description !== undefined)
        updateData.description = item.description;
      if (item.content !== undefined) updateData.content = item.content;

      // For now, we only support updating snippet metadata
      if (Object.keys(updateData).length === 0) {
        throw new Error("No data provided for update");
      }

      // Add updated_at timestamp
      updateData.updated_at = new Date().toISOString();

      const { data, error: updateError } = await supabase
        .from("knowledge_items")
        .update(updateData)
        .eq("id", item.id)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      toast({
        title: "Knowledge item updated",
        description: "Your knowledge item has been updated successfully",
      });

      return data;
    } catch (err: any) {
      setError(err.message || "Failed to update knowledge item");
      toast({
        variant: "destructive",
        title: "Failed to update knowledge item",
        description: err.message || "An unexpected error occurred",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { updateItem, isLoading, error };
};
